/**
 * QZ Tray helper for printing to an installed Windows printer without
 * disturbing its normal driver (invoice printing keeps using the same
 * printer/driver/queue). Two ways to print:
 *  - printRawCommandToQz: raw ZPL/CPCL bytes straight to a thermal printer,
 *    bypassing the driver (fast, but the printer must natively understand
 *    that command language).
 *  - printHtmlLabelsToQz: an HTML page rendered through the printer's
 *    normal driver, for printers with no thermal command interpreter at
 *    all — a plain page-fed inkjet/laser (e.g. HP Smart Tank, Canon
 *    LBP2900) or a thermal printer in the wrong command dialect (e.g.
 *    Argox PPLA/PPLB instead of PPLZ).
 * Requires the QZ Tray desktop app running locally: https://qz.io/download/
 */
import qz from "qz-tray";

const STORAGE_KEY = "qz_selected_printer";

export function isQzConnected() {
  return qz.websocket.isActive();
}

/**
 * Connects to the local QZ Tray websocket if not already connected.
 * Throws if QZ Tray isn't running/installed.
 */
export async function connectQz() {
  if (qz.websocket.isActive()) return;
  await qz.websocket.connect();
}

/**
 * Returns the list of printer names installed on this machine (as known to
 * the OS print system), for the user to pick which one to print labels to.
 */
export async function listQzPrinters() {
  await connectQz();
  return qz.printers.find();
}

export function savePrinterName(name) {
  localStorage.setItem(STORAGE_KEY, name);
}

export function getSavedPrinterName() {
  return localStorage.getItem(STORAGE_KEY) || null;
}

/**
 * Sends a raw command string (ZPL/CPCL) to the given printer as a RAW
 * datatype job, so the driver passes it straight to the printer instead of
 * trying to render it.
 */
export async function printRawCommandToQz(printerName, rawCommandString) {
  await connectQz();
  const config = qz.configs.create(printerName);
  const data = [
    {
      type: "raw",
      format: "plain",
      data: rawCommandString,
    },
  ];
  await qz.print(config, data);
}

/**
 * Sends one or more full-page HTML renderings to the given printer as
 * "pixel" jobs, so the printer's normal installed Windows driver renders
 * and translates them — for printers with no thermal command interpreter
 * printRawCommandToQz could target. widthMm/heightMm set the physical page
 * size (e.g. A4) so each HTML page prints as one physical sheet.
 */
export async function printHtmlLabelsToQz(printerName, htmlPages, widthMm, heightMm) {
  await connectQz();
  const config = qz.configs.create(printerName, {
    units: "mm",
    size: { width: widthMm, height: heightMm },
    scaleContent: false,
  });
  const data = htmlPages.map((html) => ({
    type: "pixel",
    format: "html",
    flavor: "plain",
    data: html,
  }));
  await qz.print(config, data);
}
